/* eslint-disable max-lines */
import {
  AssessmentSyncModel,
  BusinessEntityAttributeSyncColumn,
  DataSiloAttributeSyncColumn,
  DataSubCategoryAttributeSyncColumn,
  ProcessingActivityAttributeSyncColumn,
  ProcessingPurposeSubCategoryAttributeSyncColumn,
  SubDataPointAttributeSyncColumn,
  VendorAttributeSyncColumn,
} from '@transcend-io/privacy-types';

import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';

export const AssessmentNewRowBusinessEntityModelInput = mkInput({
  name: 'AssessmentNewRowBusinessEntityModelInput',
  comment: 'Definition of the business entity model',
  fields: Object.keys(BusinessEntityAttributeSyncColumn).reduce(
    (fields, column) => ({
      ...fields,
      [column]: {
        comment:
          `ID of the assessment question that will provide the value for the "${column}"` +
          `Business Entity column, or the value itself, if not provided via a question.`,
        type: 'string',
        optional: true,
      },
    }),
    {},
  ),
});

/** Override type */
export type AssessmentNewRowBusinessEntityModelInput = SchemaToType<
  typeof AssessmentNewRowBusinessEntityModelInput
>;

export const AssessmentNewRowDataSiloModelInput = mkInput({
  name: 'AssessmentNewRowDataSiloModelInput',
  comment: 'Definition of the data silo model',
  fields: Object.keys(DataSiloAttributeSyncColumn).reduce(
    (fields, column) => ({
      ...fields,
      [column]: {
        comment:
          `ID of the assessment question that will provide the value for the "${column}"` +
          `Data System column, or the value itself, if not provided via a question.`,
        type: 'string',
        optional: true,
      },
    }),
    {},
  ),
});

/** Override type */
export type AssessmentNewRowDataSiloModelInput = SchemaToType<
  typeof AssessmentNewRowDataSiloModelInput
>;

export const AssessmentNewRowDataSubCategoryModelInput = mkInput({
  name: 'AssessmentNewRowDataSubCategoryModelInput',
  comment: 'Definition of the data sub category model',
  fields: Object.keys(DataSubCategoryAttributeSyncColumn).reduce(
    (fields, column) => ({
      ...fields,
      [column]: {
        comment:
          `ID of the assessment question that will provide the value for the "${column}"` +
          `Data SubCategory column, or the value itself, if not provided via a question.`,
        type: 'string',
        optional: true,
      },
    }),
    {},
  ),
});

/** Override type */
export type AssessmentNewRowDataSubCategoryModelInput = SchemaToType<
  typeof AssessmentNewRowDataSubCategoryModelInput
>;

export const AssessmentNewRowProcessingActivityModelInput = mkInput({
  name: 'AssessmentNewRowProcessingActivityModelInput',
  comment: 'Definition of the processing activity model',
  fields: Object.keys(ProcessingActivityAttributeSyncColumn).reduce(
    (fields, column) => ({
      ...fields,
      [column]: {
        comment:
          `ID of the assessment question that will provide the value for the "${column}"` +
          `Processing Activity column, or the value itself, if not provided via a question.`,
        type: 'string',
        optional: true,
      },
    }),
    {},
  ),
});

/** Override type */
export type AssessmentNewRowProcessingActivityModelInput = SchemaToType<
  typeof AssessmentNewRowProcessingActivityModelInput
>;

export const AssessmentNewRowSubCategoryModelInput = mkInput({
  name: 'AssessmentNewRowSubCategoryModelInput',
  comment: 'Definition of the processing purpose subcategory model',
  fields: Object.keys(ProcessingPurposeSubCategoryAttributeSyncColumn).reduce(
    (fields, column) => ({
      ...fields,
      [column]: {
        comment:
          `ID of the assessment question that will provide the value for the "${column}"` +
          `Processing Purpose SubCategory column, or the value itself, if not provided via a question.`,
        type: 'string',
        optional: true,
      },
    }),
    {},
  ),
});

/** Override type */
export type AssessmentNewRowSubCategoryModelInput = SchemaToType<
  typeof AssessmentNewRowSubCategoryModelInput
>;

export const AssessmentNewRowSubDataPointModelInput = mkInput({
  name: 'AssessmentNewRowSubDataPointModelInput',
  comment: 'Definition of the sub data point model',
  fields: Object.keys(SubDataPointAttributeSyncColumn).reduce(
    (fields, column) => ({
      ...fields,
      [column]: {
        comment:
          `ID of the assessment question that will provide the value for the "${column}"` +
          `Sub Data Point column, or the value itself, if not provided via a question.`,
        type: 'string',
        optional: true,
      },
    }),
    {},
  ),
});

/** Override type */
export type AssessmentNewRowSubDataPointModelInput = SchemaToType<
  typeof AssessmentNewRowSubDataPointModelInput
>;

export const AssessmentNewRowVendorModelInput = mkInput({
  name: 'AssessmentNewRowVendorModelInput',
  comment: 'Definition of the vendor model',
  fields: Object.keys(VendorAttributeSyncColumn).reduce(
    (fields, column) => ({
      ...fields,
      [column]: {
        comment:
          `ID of the assessment question that will provide the value for the "${column}"` +
          `Vendor column, or the value itself, if not provided via a question.`,
        type: 'string',
        optional: true,
      },
    }),
    {},
  ),
});

/** Override type */
export type AssessmentNewRowVendorModelInput = SchemaToType<
  typeof AssessmentNewRowVendorModelInput
>;

export const AssessmentNewRowsInput = mkInput({
  name: 'AssessmentNewRowsInput',
  comment: 'Definition of new rows to create when an assessment is approved',
  fields: {
    [AssessmentSyncModel.BusinessEntity]: {
      comment:
        'Values to populate when creating a new Business Entity from an assessment',
      type: AssessmentNewRowBusinessEntityModelInput,
      optional: true,
    },
    [AssessmentSyncModel.DataSilo]: {
      comment:
        'Values to populate when creating a new Data System from an assessment',
      type: AssessmentNewRowDataSiloModelInput,
      optional: true,
    },
    [AssessmentSyncModel.DataSubCategory]: {
      comment:
        'Values to populate when creating a new Data Subcategory from an assessment',
      type: AssessmentNewRowDataSubCategoryModelInput,
      optional: true,
    },
    [AssessmentSyncModel.ProcessingActivity]: {
      comment:
        'Values to populate when creating a new Processing Activity from an assessment',
      type: AssessmentNewRowProcessingActivityModelInput,
      optional: true,
    },
    [AssessmentSyncModel.ProcessingPurposeSubCategory]: {
      comment:
        'Values to populate when creating a new Processing Purpose Subcategory from an assessment',
      type: AssessmentNewRowSubCategoryModelInput,
      optional: true,
    },
    [AssessmentSyncModel.SubDataPoint]: {
      comment:
        'Values to populate when creating a new Sub Data Point from an assessment',
      type: AssessmentNewRowSubDataPointModelInput,
      optional: true,
    },
    [AssessmentSyncModel.Vendor]: {
      comment:
        'Values to populate when creating a new Vendor from an assessment',
      type: AssessmentNewRowVendorModelInput,
      optional: true,
    },
  },
});

/** Override type */
export type AssessmentNewRowsInput = SchemaToType<
  typeof AssessmentNewRowsInput
>;

export const AssessmentNewRowBusinessEntityModel = mkType({
  name: 'AssessmentNewRowBusinessEntityModel',
  comment: 'Definition of the business entity model',
  fields: AssessmentNewRowBusinessEntityModelInput.fields,
});

/** Override type */
export type AssessmentNewRowBusinessEntityModel = SchemaToType<
  typeof AssessmentNewRowBusinessEntityModel
>;

export const AssessmentNewRowDataSiloModel = mkType({
  name: 'AssessmentNewRowDataSiloModel',
  comment: 'Definition of the data silo model',
  fields: AssessmentNewRowDataSiloModelInput.fields,
});

/** Override type */
export type AssessmentNewRowDataSiloModel = SchemaToType<
  typeof AssessmentNewRowDataSiloModel
>;

export const AssessmentNewRowDataSubCategoryModel = mkType({
  name: 'AssessmentNewRowDataSubCategoryModel',
  comment: 'Definition of the data sub category model',
  fields: AssessmentNewRowDataSubCategoryModelInput.fields,
});

/** Override type */
export type AssessmentNewRowDataSubCategoryModel = SchemaToType<
  typeof AssessmentNewRowDataSubCategoryModel
>;

export const AssessmentNewRowProcessingActivityModel = mkType({
  name: 'AssessmentNewRowProcessingActivityModel',
  comment: 'Definition of the processing activity model',
  fields: AssessmentNewRowProcessingActivityModelInput.fields,
});

/** Override type */
export type AssessmentNewRowProcessingActivityModel = SchemaToType<
  typeof AssessmentNewRowProcessingActivityModel
>;

export const AssessmentNewRowSubCategoryModel = mkType({
  name: 'AssessmentNewRowSubCategoryModel',
  comment: 'Definition of the processing purpose subcategory model',
  fields: AssessmentNewRowSubCategoryModelInput.fields,
});

/** Override type */
export type AssessmentNewRowSubCategoryModel = SchemaToType<
  typeof AssessmentNewRowSubCategoryModel
>;

export const AssessmentNewRowSubDataPointModel = mkType({
  name: 'AssessmentNewRowSubDataPointModel',
  comment: 'Definition of the sub data point model',
  fields: AssessmentNewRowSubDataPointModelInput.fields,
});

/** Override type */
export type AssessmentNewRowSubDataPointModel = SchemaToType<
  typeof AssessmentNewRowSubDataPointModel
>;

export const AssessmentNewRowVendorModel = mkType({
  name: 'AssessmentNewRowVendorModel',
  comment: 'Definition of the vendor model',
  fields: AssessmentNewRowVendorModelInput.fields,
});

/** Override type */
export type AssessmentNewRowVendorModel = SchemaToType<
  typeof AssessmentNewRowVendorModel
>;

export const AssessmentNewRows = mkType({
  name: 'AssessmentNewRows',
  comment: 'Definition of new rows to create when an assessment is approved',
  fields: {
    [AssessmentSyncModel.BusinessEntity]: {
      comment:
        'Values to populate when creating a new Business Entity from an assessment',
      type: AssessmentNewRowBusinessEntityModel,
      optional: true,
    },
    [AssessmentSyncModel.DataSilo]: {
      comment:
        'Values to populate when creating a new Data System from an assessment',
      type: AssessmentNewRowDataSiloModel,
      optional: true,
    },
    [AssessmentSyncModel.DataSubCategory]: {
      comment:
        'Values to populate when creating a new Data Subcategory from an assessment',
      type: AssessmentNewRowDataSubCategoryModel,
      optional: true,
    },
    [AssessmentSyncModel.ProcessingActivity]: {
      comment:
        'Values to populate when creating a new Processing Activity from an assessment',
      type: AssessmentNewRowProcessingActivityModel,
      optional: true,
    },
    [AssessmentSyncModel.ProcessingPurposeSubCategory]: {
      comment:
        'Values to populate when creating a new Processing Purpose Subcategory from an assessment',
      type: AssessmentNewRowSubCategoryModel,
      optional: true,
    },
    [AssessmentSyncModel.SubDataPoint]: {
      comment:
        'Values to populate when creating a new Sub Data Point from an assessment',
      type: AssessmentNewRowSubDataPointModel,
      optional: true,
    },
    [AssessmentSyncModel.Vendor]: {
      comment:
        'Values to populate when creating a new Vendor from an assessment',
      type: AssessmentNewRowVendorModel,
      optional: true,
    },
  },
});

/** Override type */
export type AssessmentNewRows = SchemaToType<typeof AssessmentNewRows>;
/* eslint-enable max-lines */
